import type { ReactNode } from "react";
import type { SiteConfig } from "../../api/types";
import { charityLabel, charitySentence, pledgeText } from "../../lib/charity";
import {
  AT_LAUNCH,
  CLAIM_GAS,
  GRADUATION,
  INDEXSY_MINT,
  LAUNCH_FEE,
  PAIR_FALLBACK,
  PROMOTION_FALLBACK,
} from "../../lib/facts";
import {
  creatorShare,
  platformFee,
  pledgeCeiling,
  pledgeExample,
  tradersPay,
} from "../../lib/fees";

export interface TermsSection {
  id: string;
  title: string;
  body: ReactNode;
}

/** Facts only. Every sentence here is checkable on chain or from config. */
export function termsSections(config: SiteConfig | null): TermsSection[] {
  const pair = config?.pair.symbol ?? PAIR_FALLBACK;
  const charity = charityLabel(config);
  const devBuyNote = config?.team.devBuyNote ?? null;
  const burnTx = config?.team.burnTx ?? null;
  const poolNote = config?.pool.note ?? null;
  const wallet = config?.team.wallet ?? null;
  const promotion = config?.team.promotion ?? PROMOTION_FALLBACK;

  return [
    {
      id: "what-it-is",
      title: "What $TISM is",
      body: (
        <>
          <p className="body">
            $TISM is a memecoin. It launches on Robinhood Chain, chain id 4663, through the Pons v2
            launchpad, paired with {pair}.
          </p>
          <p className="body">
            Buys and sells are in {pair}. The bonding curve graduates once it has raised{" "}
            {GRADUATION}. Liquidity then moves to a Uniswap v4 TISM/{pair} pool and is locked
            permanently.
          </p>
          <p className="body">
            Creating the token costs {LAUNCH_FEE}. Every trade pays a platform fee of{" "}
            {platformFee(config)} plus a creator tax the creator set once at launch, which goes to
            the creator wallet. The total traders pay is {tradersPay(config)}, and the creator's
            share is {creatorShare(config)}.
          </p>
          <p className="body">
            After launch there is no mint, no freeze, no blacklist, and no way to change the fee.
          </p>
        </>
      ),
    },
    {
      id: "what-it-is-not",
      title: "What it is not",
      body: (
        <>
          <p className="body">
            $TISM has no intrinsic value. It carries no rights, no revenue share and no fee share to
            holders, and there is no roadmap. Nothing is promised. The price can go to zero.
          </p>
          <p className="body">
            Nothing on this site is investment advice. Holding $TISM does not make you a donor to
            any charity and gives you no claim on the pledge below.
          </p>
        </>
      ),
    },
    {
      id: "the-pledge",
      title: "The pledge",
      body: (
        <>
          <p className="body">
            {pledgeText(config)}, for as long as the token trades. Fees are collected in ETH, sold,
            and donated in Canadian dollars every quarter, to {charity}. There is no minimum and no
            maximum.
          </p>
          <p className="body">
            Half of the creator's share is what gets donated, so the pledge is{" "}
            {pledgeCeiling(config)}. In money, {pledgeExample(config)}. If nothing trades, nothing
            is donated.
          </p>
          <p className="body">{charitySentence(config)}</p>
          <p className="body">
            No donation receipts are available to buyers or claimants. The donation is made by the
            team, not by you.
          </p>
        </>
      ),
    },
    {
      id: "the-airdrop",
      title: "The airdrop",
      body: (
        <>
          <p className="body">
            The airdrop is free. It goes to the top 200 $INDEXSY holders at a snapshot taken before
            any announcement. $INDEXSY is a Solana token; its mint is{" "}
            <span className="mono">{INDEXSY_MINT}</span>.
          </p>
          <p className="body">
            There is nothing to do except prove the wallet with Phantom and register an address with
            MetaMask. Claiming costs the holder {CLAIM_GAS} in gas on Robinhood Chain. There are no
            tasks, no posting and no referrals, ever.
          </p>
          <p className="body">
            The snapshot list is fixed. Buying $INDEXSY now does not add a wallet to it, and moving
            $INDEXSY does not take one off it.
          </p>
        </>
      ),
    },
    {
      id: "team-holdings",
      title: "The team's holdings",
      body: (
        <>
          <p className="body">
            The team is the people behind indexsy.com. The team made a developer buy at launch.
          </p>
          <p className="body">Developer buy: {devBuyNote ?? AT_LAUNCH}</p>
          <p className="body">
            Burn transaction:{" "}
            {burnTx ? <span className="mono">{burnTx}</span> : "none recorded"}.
          </p>
          <p className="body">Airdrop pool: {poolNote ?? "published once it is funded"}</p>
          <p className="body">
            Creator wallet: {wallet ? <span className="mono">{wallet}</span> : AT_LAUNCH}.
          </p>
          <p className="body">
            All of the above is published on this site from the server, so it changes the moment
            the facts do.
          </p>
        </>
      ),
    },
    {
      id: "promotion",
      title: "Promotion",
      body: (
        <>
          <p className="body">{promotion}</p>
          <p className="body">
            Anyone who says they were paid by the team to post about $TISM, or that they hold an
            official partnership, is not telling the truth.
          </p>
        </>
      ),
    },
    {
      id: "availability",
      title: "Availability",
      body: (
        <>
          <p className="body">
            $TISM is not offered to anyone in a place where that would be unlawful. Knowing the
            rules where you are is on you.
          </p>
          <p className="body">
            Nothing here is investment advice. $TISM carries no rights, no revenue share and no fee
            share.
          </p>
        </>
      ),
    },
    {
      id: "official-channels",
      title: "Official channels",
      body: (
        <>
          <p className="body">
            This site is the only official channel. Nobody from the team messages anyone first, and
            nobody will ever ask for a seed phrase or a private key.
          </p>
          <p className="body">
            The token address, the distributor address, the creator wallet and the Pons listing link
            are all published on this site from the server, so they update without a site deploy.
          </p>
        </>
      ),
    },
  ];
}
